import React, { useState, useEffect, useRef, ReactNode } from 'react';

interface LazySectionProps {
  children: ReactNode;
  /** Minimum height placeholder before content is loaded (prevents CLS) */
  minHeight?: string;
  /** How far before the section enters viewport to start rendering (px) */
  rootMargin?: string;
  /** Optional className for the wrapper div */
  className?: string;
  /** If true, adds a fade-in animation when section appears */
  animate?: boolean;
}

/**
 * LazySection — renders children only when they enter the viewport.
 * 
 * This dramatically improves LCP and TBT on landing pages by deferring
 * the rendering of below-the-fold sections (pricing, FAQ, testimonials, etc.)
 * until the user scrolls near them.
 * 
 * Usage:
 *   <LazySection minHeight="400px">
 *     <PricingSection />
 *   </LazySection>
 */
const LazySection: React.FC<LazySectionProps> = ({
  children,
  minHeight = '200px',
  rootMargin = '300px',
  className = '',
  animate = true,
}) => {
  // During prerendering (Puppeteer), render everything immediately for SEO
  const isPrerender = typeof window !== 'undefined' && (window as any).__PRERENDER__;
  const [isVisible, setIsVisible] = useState(isPrerender);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    // If IntersectionObserver is not available, render immediately
    if (typeof IntersectionObserver === 'undefined') {
      setIsVisible(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect(); // Once visible, stop observing
        }
      },
      {
        rootMargin: `${rootMargin} 0px ${rootMargin} 0px`,
        threshold: 0,
      }
    );

    observer.observe(element);

    return () => observer.disconnect();
  }, [rootMargin]);

  if (isVisible) {
    return (
      <div
        ref={ref}
        className={className}
        style={animate ? {
          animation: 'lazySectionFadeIn 0.4s ease-out',
        } : undefined}
      >
        {children}
      </div>
    );
  }

  return (
    <div
      ref={ref}
      className={className}
      style={{ minHeight }}
      aria-hidden="true"
    />
  );
};

export default LazySection;
